import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import { TimeGranularity } from './dashboard.service';
import type { UserAnalyticsFilters } from '@/types/courseAssignmentAnalytics.types';
import * as XLSX from 'xlsx';

export interface ExportActivityReportParams {
  searchQuery?: string;
  selectedStatus?: string;
  timeRange?: string;
  startDate?: string;
  endDate?: string;
  granularity?: TimeGranularity;
}

export interface ExportUserAnalyticsParams extends Omit<UserAnalyticsFilters, 'page' | 'limit'> {
  // Extends UserAnalyticsFilters but removes pagination params
  assigned_certifications?: string;
  cert_assigned_from?: string;
  cert_assigned_to?: string;
  cert_completed_from?: string;
  cert_completed_to?: string;
  teamwork_filter?: 'all' | 'bu_head' | 'reporting_manager' | 'hrbp';
  reportName?: string; // Custom report name for filename (e.g., 'team-progress', 'employee-progress')
}

export interface ExportGroupAnalyticsParams {
  search?: string;
  engagementLevel?: string;
  completionRange?: string;
}

export interface ExportRoleAnalyticsParams {
  search?: string;
  engagementLevel?: string;
  completionRange?: string;
}

export interface ExportFilenameOptions {
  timeRange: string;
  startDate?: string;
  endDate?: string;
  selectedStatus?: string;
  searchQuery?: string;
}

export interface ExportUsersListParams {
  search?: string;
  status?: string;
  group_key?: string;
  role_key?: string;
  email_verified?: boolean;
  mobile_verified?: boolean;
}

export interface ExportInvitationsParams {
  search?: string;
  status?: string;
  email?: string;
  group_key?: string;
  invited_by?: string;
  sort_by?: 'invited_at' | 'full_name' | 'status';
  sort_order?: 'asc' | 'desc';
}

export interface ExportUserCourseProgressParams {
  search?: string;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  course_id?: string;
  assignment_id?: string;
  sort_by?: 'last_accessed_at' | 'progress_percentage' | 'deadline_date' | 'created_at';
  sort_order?: 'asc' | 'desc';
}

export interface ExportUserCertificationProgressParams {
  search?: string;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  certification_id?: string;
  assignment_id?: string;
  sort_by?: 'LAST_ACCESSED' | 'PROGRESS' | 'DEADLINE' | 'CREATED_AT';
  sort_order?: 'asc' | 'desc';
}

export interface ExportAuditTrailParams {
  event_type?: string;
  start_date?: string;
  end_date?: string;
  email?: string;
  name?: string;
  search?: string;
  include_archived?: boolean;
}

export interface ExportReviewsParams {
  review_type?: 'course' | 'certificate' | 'learning_path';
  status?: 'approved' | 'flagged' | 'deleted';
  rating?: number;
  search?: string;
}

export interface ExportCertificationPerformanceParams {
  timeRange?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  start_date?: string;
  end_date?: string;
  groupKey?: string;
  roleKey?: string;
}

/**
 * Export Service
 * Handles all export-related operations including activity reports export
 */
class ExportService {
  /**
   * Generate filename for export with filters
   */
  generateExportFilename(options: ExportFilenameOptions): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['activity-report'];

    // Add time range/granularity
    if (options.timeRange === 'custom' && options.startDate && options.endDate) {
      filenameParts.push(`custom-${options.startDate}-to-${options.endDate}`);
    } else if (options.timeRange === 'all') {
      filenameParts.push('all-time');
    } else {
      filenameParts.push(options.timeRange);
    }

    // Add status filter if not 'all'
    if (options.selectedStatus && options.selectedStatus !== 'all') {
      filenameParts.push(`status-${options.selectedStatus}`);
    }

    // Add search query if applied
    if (options.searchQuery) {
      const sanitizedSearch = options.searchQuery.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    return filenameParts.join('_') + '.csv';
  }

  /**
   * Generate filename for user analytics export with filters
   */
  generateUserAnalyticsFilename(
    format: 'csv' | 'excel',
    filters: ExportUserAnalyticsParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const reportName = filters.reportName || 'employee-progress';
    const filenameParts = [reportName];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add department filter (groupKey)
    if (filters.groupKey) {
      filenameParts.push(`dept-${filters.groupKey}`);
    }

    // Add role filter
    if (filters.roleKey) {
      filenameParts.push(`role-${filters.roleKey}`);
    }

    // Add course filter
    if (filters.courseId) {
      filenameParts.push(`course-${filters.courseId.substring(0, 8)}`);
    }

    // Add certification filter
    if (filters.assigned_certifications) {
      filenameParts.push(`cert-${filters.assigned_certifications.substring(0, 8)}`);
    }

    // Add status filter if not 'all'
    if (filters.status && (filters.status as string) !== 'all') {
      filenameParts.push(`status-${filters.status}`);
    }

    // Add engagement filter if not 'all'
    if (filters.engagement && (filters.engagement as string) !== 'all') {
      filenameParts.push(`engagement-${filters.engagement}`);
    }

    // Add team view filter if not 'all'
    if (filters.teamwork_filter && filters.teamwork_filter !== 'all') {
      const teamViewLabels: Record<string, string> = {
        'bu_head': 'business-unit',
        'reporting_manager': 'direct-reports',
        'hrbp': 'hr-business-partner'
      };
      filenameParts.push(`team-${teamViewLabels[filters.teamwork_filter] || filters.teamwork_filter}`);
    }

    // Add organizational hierarchy filters
    if (filters.selected_bu_head_email) {
      const sanitizedEmail = filters.selected_bu_head_email.split('@')[0].replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`bu-head-${sanitizedEmail}`);
    }

    if (filters.selected_rm_email) {
      const sanitizedEmail = filters.selected_rm_email.split('@')[0].replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`rm-${sanitizedEmail}`);
    }

    if (filters.selected_hrbp_email) {
      const sanitizedEmail = filters.selected_hrbp_email.split('@')[0].replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`hrbp-${sanitizedEmail}`);
    }

    // Add require assignments filter indicator
    if (filters.require_assignments) {
      filenameParts.push('assigned-only');
    }

    // Add sort filter
    if (filters.sort) {
      filenameParts.push(`sort-${filters.sort}`);
    }

    // Add course date ranges
    if (filters.created_from && filters.created_to) {
      filenameParts.push(`course-assigned-${filters.created_from}-to-${filters.created_to}`);
    }

    if (filters.completed_from && filters.completed_to) {
      filenameParts.push(`course-completed-${filters.completed_from}-to-${filters.completed_to}`);
    }

    // Add certification date ranges
    if (filters.cert_assigned_from && filters.cert_assigned_to) {
      filenameParts.push(`cert-assigned-${filters.cert_assigned_from}-to-${filters.cert_assigned_to}`);
    }

    if (filters.cert_completed_from && filters.cert_completed_to) {
      filenameParts.push(`cert-completed-${filters.cert_completed_from}-to-${filters.cert_completed_to}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate filename for group/department analytics export with filters
   */
  generateGroupAnalyticsFilename(
    format: 'csv' | 'excel',
    filters: ExportGroupAnalyticsParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['department-progress'];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add engagement filter if not 'all'
    if (filters.engagementLevel && filters.engagementLevel !== 'all') {
      filenameParts.push(`engagement-${filters.engagementLevel}`);
    }

    // Add completion range filter if not 'all'
    if (filters.completionRange && filters.completionRange !== 'all') {
      filenameParts.push(`completion-${filters.completionRange}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate filename for role analytics export with filters
   */
  generateRoleAnalyticsFilename(
    format: 'csv' | 'excel',
    filters: ExportRoleAnalyticsParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['role-progress'];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add engagement filter if not 'all'
    if (filters.engagementLevel && filters.engagementLevel !== 'all') {
      filenameParts.push(`engagement-${filters.engagementLevel}`);
    }

    // Add completion range filter if not 'all'
    if (filters.completionRange && filters.completionRange !== 'all') {
      filenameParts.push(`completion-${filters.completionRange}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV data from activities
   */
  // Build an .xlsx blob from activity records (columns match generateActivityCSV).
  private generateActivityExcel(activitiesData: any[]): Blob {
    const headers = [
      'Timestamp', 'Status', 'Activity Type', 'Action', 'Details',
      'User Name', 'User Email', 'User ID', 'Performed By Name', 'Performed By Email',
      'Browser', 'Browser Version', 'OS', 'OS Version', 'Device', 'IP Address', 'Activity ID',
    ];
    const rows = activitiesData.map(activity => [
      new Date(activity.timestamp).toISOString(),
      activity.status,
      activity.type,
      activity.action,
      activity.details,
      activity.metadata?.user_name || '',
      activity.metadata?.user_email || '',
      activity.metadata?.user_id || '',
      activity.metadata?.action_by_name || 'Self',
      activity.metadata?.action_by_email || '',
      activity.device_info?.browser || 'Unknown',
      activity.device_info?.browser_version || '',
      activity.device_info?.os || 'Unknown',
      activity.device_info?.os_version || '',
      activity.device_info?.device || 'Unknown',
      activity.metadata?.ip_address || 'Unknown',
      activity._id,
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Activity Report');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  generateActivityCSV(activitiesData: any[]): string {
    const headers = [
      'Timestamp',
      'Status',
      'Activity Type',
      'Action',
      'Details',
      'User Name',
      'User Email',
      'User ID',
      'Performed By Name',
      'Performed By Email',
      'Browser',
      'Browser Version',
      'OS',
      'OS Version',
      'Device',
      'IP Address',
      'Activity ID'
    ];

    const rows = activitiesData.map(activity => [
      new Date(activity.timestamp).toISOString(),
      activity.status,
      activity.type,
      activity.action,
      activity.details,
      activity.metadata?.user_name || '',
      activity.metadata?.user_email || '',
      activity.metadata?.user_id || '',
      activity.metadata?.action_by_name || 'Self',
      activity.metadata?.action_by_email || '',
      activity.device_info?.browser || 'Unknown',
      activity.device_info?.browser_version || '',
      activity.device_info?.os || 'Unknown',
      activity.device_info?.os_version || '',
      activity.device_info?.device || 'Unknown',
      activity.metadata?.ip_address || 'Unknown',
      activity._id
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => this.sanitizeCsvCell(cell)).join(','))
    ].join('\n');
  }

  /**
   * Download CSV string as file with proper encoding
   */
  downloadCSV(csvContent: string, filename: string): void {
    // Add UTF-8 BOM for proper Excel encoding
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export activity report to CSV
   * Calls the export API endpoint with filter parameters only (no skip/limit)
   */
  async exportActivityReport(params: ExportActivityReportParams, format: 'csv' | 'excel' = 'csv'): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const { searchQuery, selectedStatus, timeRange, startDate, endDate, granularity } = params;

    // Build API params - only include filters, no skip/limit
    const apiParams: any = {};

    // Add search filter
    if (searchQuery && searchQuery.length > 0) {
      apiParams.action = searchQuery;
    }

    // Add status filter
    if (selectedStatus && selectedStatus !== 'all') {
      apiParams.status = selectedStatus;
    }

    // Add granularity or custom dates
    if (timeRange === 'custom' && startDate && endDate) {
      apiParams.start_date = startDate;
      apiParams.end_date = endDate;
    } else if (timeRange !== 'all' && granularity) {
      // Only add granularity if not 'all' time range
      apiParams.granularity = granularity;
    }

    // Tell the backend which format is being downloaded so the audit logs it correctly.
    apiParams._export_format = format === 'excel' ? 'EXCEL' : 'CSV';

    // Call export API endpoint with POST - expecting JSON response
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.ACTIVITY_REPORT, apiParams);

    // Extract activities data from response
    const activitiesData = response.data?.data || response.data || [];

    if (activitiesData.length === 0) {
      throw new Error('No activities found to export');
    }

    // Generate filename with filters
    const filename = this.generateExportFilename({
      timeRange: timeRange || 'all',
      startDate,
      endDate,
      selectedStatus,
      searchQuery
    });

    // Download in the chosen format
    if (format === 'excel') {
      const excelBlob = this.generateActivityExcel(activitiesData);
      this.downloadExcel(excelBlob, filename.replace(/\.csv$/i, '') + '.xlsx');
    } else {
      const csvContent = this.generateActivityCSV(activitiesData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: activitiesData.length
    };
  }

  /**
   * Format time in seconds to HH:MM:SS
   */
  private formatTimeSpent(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Sanitize a CSV cell value: escape quotes and prevent formula injection.
   * Cells starting with =, +, -, @ can trigger formula execution in Excel.
   */
  private sanitizeCsvCell(cell: string | null | undefined): string {
    let value = (cell || '').toString().replace(/"/g, '""');
    if (/^[=+\-@]/.test(value)) {
      value = '\t' + value;
    }
    return `"${value}"`;
  }

  /**
   * Format date to readable string
   */
  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const hh = String(hours).padStart(2, '0');
      return `${dd}/${mm}/${yyyy}, ${hh}:${minutes} ${ampm}`;
    } catch (error) {
      return 'N/A';
    }
  }

  /**
   * Generate CSV from user analytics data
   */
  private generateUserAnalyticsCSV(users: any[]): string {
    // Define headers
    const headers = [
      'Employee',
      'Employee ID',
      'Department',
      'Courses Assigned',
      'Completed Courses',
      'Progress (%)',
      'Completion Percentage (%)',
      'Time Spent for Course (HH:MM:SS)',
      'Certifications Assigned',
      'Certifications Completed',
      'Time Spent for Certification (HH:MM:SS)',
      'Streak (Current)',
      'Engagement',
      'Last Active'
    ];

    // Generate rows
    const rows = users.map(user => [
      user.userName || 'N/A',
      user.employeeID || 'N/A',
      user.department || 'N/A',
      user.assignedCourses || 0,
      user.completedCourseCount || 0,
      user.progress?.toFixed(2) || '0.00',
      user.completedPercentage?.toFixed(2) || '0.00',
      this.formatTimeSpent(user.timeSpent || 0),
      user.certification_count || 0,
      user.completed_certification_count || 0,
      this.formatTimeSpent(user.certification_time_spent || 0),
      user.streak?.current || 0,
      user.engagement || 'low',
      this.formatDate(user.lastActive || '')
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from user analytics data
   */
  private generateUserAnalyticsExcel(users: any[]): Blob {
    // Define headers
    const headers = [
      'Employee',
      'Employee ID',
      'Department',
      'Courses Assigned',
      'Completed Courses',
      'Progress (%)',
      'Completion Percentage (%)',
      'Time Spent for Course (HH:MM:SS)',
      'Certifications Assigned',
      'Certifications Completed',
      'Time Spent for Certification (HH:MM:SS)',
      'Streak (Current)',
      'Engagement',
      'Last Active'
    ];

    // Generate rows
    const rows = users.map(user => [
      user.userName || 'N/A',
      user.employeeID || 'N/A',
      user.department || 'N/A',
      user.assignedCourses || 0,
      user.completedCourseCount || 0,
      user.progress?.toFixed(2) || '0.00',
      user.completedPercentage?.toFixed(2) || '0.00',
      this.formatTimeSpent(user.timeSpent || 0),
      user.certification_count || 0,
      user.completed_certification_count || 0,
      this.formatTimeSpent(user.certification_time_spent || 0),
      user.streak?.current || 0,
      user.engagement || 'low',
      this.formatDate(user.lastActive || '')
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 25 }, // Employee
      { wch: 15 }, // Employee ID
      { wch: 30 }, // Department
      { wch: 18 }, // Courses Assigned
      { wch: 18 }, // Completed Courses
      { wch: 15 }, // Progress (%)
      { wch: 22 }, // Completion Percentage (%)
      { wch: 30 }, // Time Spent for Course
      { wch: 23 }, // Certifications Assigned
      { wch: 23 }, // Certifications Completed
      { wch: 35 }, // Time Spent for Certification
      { wch: 15 }, // Streak (Current)
      { wch: 12 }, // Engagement
      { wch: 20 }  // Last Active
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Analytics');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Download Excel blob as file
   */
  private downloadExcel(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export user analytics to CSV or Excel
   * Calls the backend API with filters (no pagination) and generates file from JSON response
   */
  async exportUserAnalytics(
    format: 'csv' | 'excel',
    filters: ExportUserAnalyticsParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - include all filters but NO skip/limit (pagination)
    const apiParams: any = {};

    // Add all filters from the UI
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.status && (filters.status as string) !== 'all') {
      apiParams.status = filters.status;
    }

    if (filters.engagement && (filters.engagement as string) !== 'all') {
      apiParams.engagement = filters.engagement;
    }

    if (filters.sort) {
      apiParams.sort = filters.sort;
    }

    if (filters.groupKey) {
      apiParams.groupKey = filters.groupKey;
    }

    if (filters.roleKey) {
      apiParams.roleKey = filters.roleKey;
    }

    if (filters.courseId) {
      apiParams.courseId = filters.courseId;
    }

    if (filters.assigned_certifications) {
      apiParams.assigned_certifications = filters.assigned_certifications;
    }

    // Course date filters
    if (filters.created_from) {
      apiParams.created_from = filters.created_from;
    }

    if (filters.created_to) {
      apiParams.created_to = filters.created_to;
    }

    if (filters.completed_from) {
      apiParams.completed_from = filters.completed_from;
    }

    if (filters.completed_to) {
      apiParams.completed_to = filters.completed_to;
    }

    // Certification date filters
    if (filters.cert_assigned_from) {
      apiParams.cert_assigned_from = filters.cert_assigned_from;
    }

    if (filters.cert_assigned_to) {
      apiParams.cert_assigned_to = filters.cert_assigned_to;
    }

    if (filters.cert_completed_from) {
      apiParams.cert_completed_from = filters.cert_completed_from;
    }

    if (filters.cert_completed_to) {
      apiParams.cert_completed_to = filters.cert_completed_to;
    }

    // Teamwork filter - always send (default 'all')
    if (filters.teamwork_filter) {
      apiParams.teamwork_filter = filters.teamwork_filter;
    }

    // Organizational hierarchy filters
    if (filters.selected_bu_head_email) {
      apiParams.selected_bu_head_email = filters.selected_bu_head_email;
    }
    if (filters.selected_rm_email) {
      apiParams.selected_rm_email = filters.selected_rm_email;
    }
    if (filters.selected_hrbp_email) {
      apiParams.selected_hrbp_email = filters.selected_hrbp_email;
    }

    // Require assignments filter
    if (filters.require_assignments !== undefined) {
      apiParams.require_assignments = filters.require_assignments;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.USER_ANALYTICS, apiParams);

    // Extract user data from response
    const usersData = response.data?.data?.users || response.data?.users || [];

    if (usersData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No data available to export with the current filters'
      };
    }

    // Generate filename with filters
    const filename = this.generateUserAnalyticsFilename(format, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      // Generate Excel file
      const excelBlob = this.generateUserAnalyticsExcel(usersData);
      this.downloadExcel(excelBlob, filename);
    } else {
      // Generate CSV file
      const csvContent = this.generateUserAnalyticsCSV(usersData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: usersData.length
    };
  }

  /**
   * Generate CSV from group analytics data
   */
  private generateGroupAnalyticsCSV(groups: any[]): string {
    // Define headers
    const headers = [
      'Department',
      'Members',
      'Courses',
      'Progress (%)',
      'Course Completed',
      'Course In Progress',
      'Course Not Started',
      'Course Completion Percentage (%)',
      'Certifications',
      'Cert Completed',
      'Cert In Progress',
      'Cert Not Started',
      'Engagement'
    ];

    // Generate rows
    const rows = groups.map(group => [
      group.groupName || 'N/A',
      group.totalMembers || 0,
      group.assignedCourses || 0,
      group.avgProgress?.toFixed(2) || '0.00',
      group.memberStats?.completed || 0,
      group.memberStats?.inProgress || 0,
      group.memberStats?.notStarted || 0,
      group.completionRate?.toFixed(2) || '0.00',
      group.certification_count || 0,
      group.certification_stats?.completed || 0,
      group.certification_stats?.in_progress || 0,
      group.certification_stats?.not_started || 0,
      group.engagementLevel || 'low'
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from group analytics data
   */
  private generateGroupAnalyticsExcel(groups: any[]): Blob {
    // Define headers
    const headers = [
      'Department',
      'Members',
      'Courses',
      'Progress (%)',
      'Course Completed',
      'Course In Progress',
      'Course Not Started',
      'Course Completion Percentage (%)',
      'Certifications',
      'Cert Completed',
      'Cert In Progress',
      'Cert Not Started',
      'Engagement'
    ];

    // Generate rows
    const rows = groups.map(group => [
      group.groupName || 'N/A',
      group.totalMembers || 0,
      group.assignedCourses || 0,
      group.avgProgress?.toFixed(2) || '0.00',
      group.memberStats?.completed || 0,
      group.memberStats?.inProgress || 0,
      group.memberStats?.notStarted || 0,
      group.completionRate?.toFixed(2) || '0.00',
      group.certification_count || 0,
      group.certification_stats?.completed || 0,
      group.certification_stats?.in_progress || 0,
      group.certification_stats?.not_started || 0,
      group.engagementLevel || 'low'
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 30 }, // Department
      { wch: 12 }, // Members
      { wch: 12 }, // Courses
      { wch: 15 }, // Progress (%)
      { wch: 18 }, // Course Completed
      { wch: 18 }, // Course In Progress
      { wch: 18 }, // Course Not Started
      { wch: 30 }, // Course Completion Percentage (%)
      { wch: 15 }, // Certifications
      { wch: 15 }, // Cert Completed
      { wch: 15 }, // Cert In Progress
      { wch: 18 }, // Cert Not Started
      { wch: 15 }  // Engagement
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Group Analytics');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export group analytics to CSV or Excel
   */
  async exportGroupAnalytics(
    format: 'csv' | 'excel',
    filters: ExportGroupAnalyticsParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - include all filters but NO skip/limit (pagination)
    const apiParams: any = {};

    // Add filters
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.engagementLevel && filters.engagementLevel !== 'all') {
      apiParams.engagementLevel = filters.engagementLevel;
    }

    if (filters.completionRange && filters.completionRange !== 'all') {
      apiParams.completionRange = filters.completionRange;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.GROUP_ANALYTICS, apiParams);

    // Extract group data from response
    const groupsData = response.data?.data?.groups || response.data?.groups || [];

    if (groupsData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No data available to export with the current filters'
      };
    }

    // Generate filename with filters
    const filename = this.generateGroupAnalyticsFilename(format, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      // Generate Excel file
      const excelBlob = this.generateGroupAnalyticsExcel(groupsData);
      this.downloadExcel(excelBlob, filename);
    } else {
      // Generate CSV file
      const csvContent = this.generateGroupAnalyticsCSV(groupsData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: groupsData.length
    };
  }

  /**
   * Generate CSV from role analytics data
   */
  private generateRoleAnalyticsCSV(roles: any[]): string {
    // Define headers
    const headers = [
      'Role',
      'Users',
      'Courses',
      'Progress (%)',
      'Course Status',
      'Completed Percentage (%)',
      'Certifications',
      'Cert Completed',
      'Cert In Progress',
      'Cert Not Started',
      'Engagement'
    ];

    // Generate rows
    const rows = roles.map(role => [
      role.roleName || 'N/A',
      role.totalUsers || 0,
      role.assignedCourses || 0,
      role.avgProgress?.toFixed(2) || '0.00',
      this.getRoleStatus(role),
      role.completionRate?.toFixed(2) || '0.00',
      role.certification_count || 0,
      role.certification_stats?.completed || 0,
      role.certification_stats?.in_progress || 0,
      role.certification_stats?.not_started || 0,
      role.performanceMetrics?.engagementLevel || 'low'
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Get role status based on user stats
   */
  private getRoleStatus(role: any): string {
    const stats = role.userStats || {};
    const completed = stats.completed || 0;
    const inProgress = stats.in_progress || 0;
    const notStarted = stats.not_started || 0;

    if (completed > inProgress && completed > notStarted) return 'Completed';
    if (inProgress > 0) return 'In Progress';
    if (notStarted > 0) return 'Not Started';
    return 'N/A';
  }

  /**
   * Generate Excel file from role analytics data
   */
  private generateRoleAnalyticsExcel(roles: any[]): Blob {
    // Define headers
    const headers = [
      'Role',
      'Users',
      'Courses',
      'Progress (%)',
      'Course Status',
      'Completed Percentage (%)',
      'Certifications',
      'Cert Completed',
      'Cert In Progress',
      'Cert Not Started',
      'Engagement'
    ];

    // Generate rows
    const rows = roles.map(role => [
      role.roleName || 'N/A',
      role.totalUsers || 0,
      role.assignedCourses || 0,
      role.avgProgress?.toFixed(2) || '0.00',
      this.getRoleStatus(role),
      role.completionRate?.toFixed(2) || '0.00',
      role.certification_count || 0,
      role.certification_stats?.completed || 0,
      role.certification_stats?.in_progress || 0,
      role.certification_stats?.not_started || 0,
      role.performanceMetrics?.engagementLevel || 'low'
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 30 }, // Role
      { wch: 10 }, // Users
      { wch: 12 }, // Courses
      { wch: 15 }, // Progress (%)
      { wch: 18 }, // Course Status
      { wch: 25 }, // Completed Percentage (%)
      { wch: 15 }, // Certifications
      { wch: 15 }, // Cert Completed
      { wch: 15 }, // Cert In Progress
      { wch: 18 }, // Cert Not Started
      { wch: 15 }  // Engagement
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Role Analytics');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export role analytics to CSV or Excel
   */
  async exportRoleAnalytics(
    format: 'csv' | 'excel',
    filters: ExportRoleAnalyticsParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - include all filters but NO skip/limit (pagination)
    const apiParams: any = {};

    // Add filters
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.engagementLevel && filters.engagementLevel !== 'all') {
      apiParams.engagementLevel = filters.engagementLevel;
    }

    if (filters.completionRange && filters.completionRange !== 'all') {
      apiParams.completionRange = filters.completionRange;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.ROLE_ANALYTICS, apiParams);

    // Extract role data from response
    const rolesData = response.data?.data?.roles || response.data?.roles || [];

    if (rolesData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No data available to export with the current filters'
      };
    }

    // Generate filename with filters
    const filename = this.generateRoleAnalyticsFilename(format, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      // Generate Excel file
      const excelBlob = this.generateRoleAnalyticsExcel(rolesData);
      this.downloadExcel(excelBlob, filename);
    } else {
      // Generate CSV file
      const csvContent = this.generateRoleAnalyticsCSV(rolesData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: rolesData.length
    };
  }

  /**
   * Generate CSV from groups list data
   */
  private generateGroupsListCSV(groups: any[]): string {
    // Define headers
    const headers = [
      'Department Name',
      'Group Key',
      'Description',
      'Total Members',
      'Courses Assigned',
      'Certifications',
      'Course Assignments',
      'Certification Assignments',
      'Learning Path Assignments'
    ];

    // Generate rows
    const rows = groups.map(group => [
      group.group_name || group.groupName || 'N/A',
      group.group_key || group.groupKey || 'N/A',
      group.group_description || 'No description available',
      group.totalMembers || 0,
      group.assignedCourses || 0,
      group.certification_count || 0,
      group.group_course_assignments || 0,
      group.group_certification_assignments || 0,
      group.group_learning_path_assignments || 0
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from groups list data
   */
  private generateGroupsListExcel(groups: any[]): Blob {
    // Define headers
    const headers = [
      'Department Name',
      'Group Key',
      'Description',
      'Total Members',
      'Courses Assigned',
      'Certifications',
      'Course Assignments',
      'Certification Assignments',
      'Learning Path Assignments'
    ];

    // Generate rows
    const rows = groups.map(group => [
      group.group_name || group.groupName || 'N/A',
      group.group_key || group.groupKey || 'N/A',
      group.group_description || 'No description available',
      group.totalMembers || 0,
      group.assignedCourses || 0,
      group.certification_count || 0,
      group.group_course_assignments || 0,
      group.group_certification_assignments || 0,
      group.group_learning_path_assignments || 0
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 30 }, // Department Name
      { wch: 20 }, // Group Key
      { wch: 40 }, // Description
      { wch: 15 }, // Total Members
      { wch: 18 }, // Courses Assigned
      { wch: 15 }, // Certifications
      { wch: 20 }, // Course Assignments
      { wch: 25 }, // Certification Assignments
      { wch: 25 }  // Learning Path Assignments
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Departments');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export groups list to CSV or Excel
   * Calls the backend API without pagination params
   */
  async exportGroupsList(
    format: 'csv' | 'excel',
    searchQuery?: string
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - no skip/limit
    const apiParams: any = {};

    // Add search filter if provided
    if (searchQuery) {
      apiParams.group_name = searchQuery;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.GROUPS, apiParams);

    // Extract groups data from response
    const groupsData = response.data?.data || [];

    if (groupsData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No groups available to export'
      };
    }

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['departments'];

    if (searchQuery) {
      const sanitizedSearch = searchQuery.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    filenameParts.push(timestamp);
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    const filename = filenameParts.join('_') + `.${extension}`;

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateGroupsListExcel(groupsData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateGroupsListCSV(groupsData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: groupsData.length
    };
  }

  /**
   * Generate CSV from roles list data
   */
  private generateRolesListCSV(roles: any[]): string {
    // Define headers
    const headers = [
      'Role Name',
      'Role Key',
      'Description',
      'Total Users',
      'Courses Assigned',
      'Certifications',
      'Course Assignments',
      'Certification Assignments',
      'Learning Path Assignments'
    ];

    // Generate rows
    const rows = roles.map(role => [
      role.role_name || role.roleName || 'N/A',
      role.role_key || role.roleKey || 'N/A',
      role.role_description || 'No description available',
      role.totalUsers || 0,
      role.assignedCourses || 0,
      role.certification_count || 0,
      role.role_course_assignments || 0,
      role.role_certification_assignments || 0,
      role.role_learning_path_assignments || 0
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from roles list data
   */
  private generateRolesListExcel(roles: any[]): Blob {
    // Define headers
    const headers = [
      'Role Name',
      'Role Key',
      'Description',
      'Total Users',
      'Courses Assigned',
      'Certifications',
      'Course Assignments',
      'Certification Assignments',
      'Learning Path Assignments'
    ];

    // Generate rows
    const rows = roles.map(role => [
      role.role_name || role.roleName || 'N/A',
      role.role_key || role.roleKey || 'N/A',
      role.role_description || 'No description available',
      role.totalUsers || 0,
      role.assignedCourses || 0,
      role.certification_count || 0,
      role.role_course_assignments || 0,
      role.role_certification_assignments || 0,
      role.role_learning_path_assignments || 0
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 30 }, // Role Name
      { wch: 20 }, // Role Key
      { wch: 40 }, // Description
      { wch: 15 }, // Total Users
      { wch: 18 }, // Courses Assigned
      { wch: 15 }, // Certifications
      { wch: 20 }, // Course Assignments
      { wch: 25 }, // Certification Assignments
      { wch: 25 }  // Learning Path Assignments
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Roles');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export roles list to CSV or Excel
   * Calls the backend API without pagination params
   */
  async exportRolesList(
    format: 'csv' | 'excel',
    searchQuery?: string
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - no skip/limit
    const apiParams: any = {};

    // Add search filter if provided
    if (searchQuery) {
      apiParams.role_name = searchQuery;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.ROLES, apiParams);

    // Extract roles data from response
    const rolesData = response.data?.data || [];

    if (rolesData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No roles available to export'
      };
    }

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['roles'];

    if (searchQuery) {
      const sanitizedSearch = searchQuery.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    filenameParts.push(timestamp);
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    const filename = filenameParts.join('_') + `.${extension}`;

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateRolesListExcel(rolesData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateRolesListCSV(rolesData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: rolesData.length
    };
  }

  /**
   * Generate filename for users list export with filters
   */
  generateUsersListFilename(
    format: 'csv' | 'excel',
    filters: ExportUsersListParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['employees'];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add department filter (group_key)
    if (filters.group_key) {
      filenameParts.push(`dept-${filters.group_key}`);
    }

    // Add role filter
    if (filters.role_key) {
      filenameParts.push(`role-${filters.role_key}`);
    }

    // Add status filter if not 'all'
    if (filters.status && filters.status !== 'all') {
      filenameParts.push(`status-${filters.status}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV from users list data
   */
  private generateUsersListCSV(users: any[]): string {
    // Define headers
    const headers = [
      'Employee ID',
      'First Name',
      'Last Name',
      'Full Name',
      'Email',
      'Personal Email',
      'Mobile Number',
      'Status',
      'Departments',
      'Roles',
      'Courses Assigned',
      'Courses Completed',
      'Certifications Assigned',
      'Certifications Completed',
      'Last Login'
    ];

    // Generate rows
    const rows = users.map(user => {
      const deptNames = user.groups?.map((g: any) => g.group_name).join(' | ') || '';
      const roleNames = user.groups?.flatMap((g: any) => g.roles?.map((r: any) => r.role_name) || []).join(' | ') || '';

      return [
        user.employee_id || 'N/A',
        user.given_name || '',
        user.family_name || '',
        user.full_name || `${user.given_name || ''} ${user.family_name || ''}`.trim(),
        user.email || '',
        user.personal_email || '',
        user.mobile_number || '',
        user.status || 'N/A',
        deptNames,
        roleNames,
        user.courses?.assigned || 0,
        user.courses?.completed || 0,
        user.certifications?.assigned || 0,
        user.certifications?.completed || 0,
        user.last_login ? this.formatDate(user.last_login) : 'Never'
      ];
    });

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from users list data
   */
  private generateUsersListExcel(users: any[]): Blob {
    // Define headers
    const headers = [
      'Employee ID',
      'First Name',
      'Last Name',
      'Full Name',
      'Email',
      'Personal Email',
      'Mobile Number',
      'Status',
      'Departments',
      'Roles',
      'Courses Assigned',
      'Courses Completed',
      'Certifications Assigned',
      'Certifications Completed',
      'Last Login'
    ];

    // Generate rows
    const rows = users.map(user => {
      const deptNames = user.groups?.map((g: any) => g.group_name).join(' | ') || '';
      const roleNames = user.groups?.flatMap((g: any) => g.roles?.map((r: any) => r.role_name) || []).join(' | ') || '';

      return [
        user.employee_id || 'N/A',
        user.given_name || '',
        user.family_name || '',
        user.full_name || `${user.given_name || ''} ${user.family_name || ''}`.trim(),
        user.email || '',
        user.personal_email || '',
        user.mobile_number || '',
        user.status || 'N/A',
        deptNames,
        roleNames,
        user.courses?.assigned || 0,
        user.courses?.completed || 0,
        user.certifications?.assigned || 0,
        user.certifications?.completed || 0,
        user.last_login ? this.formatDate(user.last_login) : 'Never'
      ];
    });

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 15 }, // Employee ID
      { wch: 15 }, // First Name
      { wch: 15 }, // Last Name
      { wch: 25 }, // Full Name
      { wch: 30 }, // Email
      { wch: 30 }, // Personal Email
      { wch: 15 }, // Mobile Number
      { wch: 15 }, // Status
      { wch: 30 }, // Departments
      { wch: 25 }, // Roles
      { wch: 18 }, // Courses Assigned
      { wch: 18 }, // Courses Completed
      { wch: 22 }, // Certifications Assigned
      { wch: 22 }, // Certifications Completed
      { wch: 20 }  // Last Login
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export users list to CSV or Excel
   * Calls the backend API with filters (no pagination) and generates file from JSON response
   */
  async exportUsersList(
    format: 'csv' | 'excel',
    filters: ExportUsersListParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - include all filters but NO skip/limit (pagination)
    const apiParams: any = {};

    // Add all filters from the UI
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.status && filters.status !== 'all') {
      apiParams.status = filters.status;
    }

    if (filters.group_key && filters.group_key !== 'all') {
      apiParams.group_key = filters.group_key;
    }

    if (filters.role_key && filters.role_key !== 'all') {
      apiParams.role_key = filters.role_key;
    }

    if (filters.email_verified !== undefined) {
      apiParams.email_verified = filters.email_verified;
    }

    if (filters.mobile_verified !== undefined) {
      apiParams.mobile_verified = filters.mobile_verified;
    }

    // Tell backend which format was chosen so the audit log records CSV vs Excel.
    apiParams._export_format = format;

    // Call export API endpoint - expecting JSON response
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.USERS_LIST, apiParams);

    // Extract users data from response
    const usersData = response.data?.data?.data || response.data?.data || [];

    if (usersData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No employees available to export with the current filters'
      };
    }

    // Generate filename with filters
    const filename = this.generateUsersListFilename(format, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateUsersListExcel(usersData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateUsersListCSV(usersData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: usersData.length
    };
  }

  /**
   * Generate filename for invitations export with filters
   */
  generateInvitationsFilename(
    format: 'csv' | 'excel',
    filters: ExportInvitationsParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['invitations'];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add status filter if not 'all'
    if (filters.status && filters.status !== 'all') {
      filenameParts.push(`status-${filters.status}`);
    }

    // Add sort_by filter
    if (filters.sort_by) {
      filenameParts.push(`sort-${filters.sort_by}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV from invitations data
   */
  private generateInvitationsCSV(invitations: any[]): string {
    // Define headers
    const headers = [
      'Email',
      'Full Name',
      'Department',
      'Roles',
      'Status',
      'Invited By',
      'Invited At',
      'Expires At',
      'Registered At'
    ];

    // Generate rows
    const rows = invitations.map(invite => {
      const groupNames = invite.groups
        ? invite.groups.map((g: any) => g.group_name || g.name).join(' | ')
        : '';

      const roleNames = invite.groups
        ? invite.groups.flatMap((g: any) =>
            g.roles ? g.roles.map((r: any) => r.role_name || r.name) : []
          ).join(' | ')
        : '';

      return [
        invite.email || '',
        invite.full_name || '',
        groupNames,
        roleNames,
        invite.status || '',
        invite.invited_by_name || '',
        this.formatDate(invite.invited_at || ''),
        this.formatDate(invite.expires_at || invite.invitation_expires_at || ''),
        invite.registered_at ? this.formatDate(invite.registered_at) : ''
      ];
    });

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from invitations data
   */
  private generateInvitationsExcel(invitations: any[]): Blob {
    // Define headers
    const headers = [
      'Email',
      'Full Name',
      'Department',
      'Roles',
      'Status',
      'Invited By',
      'Invited At',
      'Expires At',
      'Registered At'
    ];

    // Generate rows
    const rows = invitations.map(invite => {
      const groupNames = invite.groups
        ? invite.groups.map((g: any) => g.group_name || g.name).join(' | ')
        : '';

      const roleNames = invite.groups
        ? invite.groups.flatMap((g: any) =>
            g.roles ? g.roles.map((r: any) => r.role_name || r.name) : []
          ).join(' | ')
        : '';

      return [
        invite.email || '',
        invite.full_name || '',
        groupNames,
        roleNames,
        invite.status || '',
        invite.invited_by_name || '',
        this.formatDate(invite.invited_at || ''),
        this.formatDate(invite.expires_at || invite.invitation_expires_at || ''),
        invite.registered_at ? this.formatDate(invite.registered_at) : ''
      ];
    });

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 30 }, // Email
      { wch: 25 }, // Full Name
      { wch: 25 }, // Department
      { wch: 25 }, // Roles
      { wch: 12 }, // Status
      { wch: 20 }, // Invited By
      { wch: 20 }, // Invited At
      { wch: 20 }, // Expires At
      { wch: 20 }  // Registered At
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invitations');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export invitations to CSV or Excel
   * Calls the backend API with filters (no pagination) and generates file from JSON response
   */
  async exportInvitations(
    format: 'csv' | 'excel',
    filters: ExportInvitationsParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - include all filters but NO skip/limit (pagination)
    const apiParams: any = {
      sort_by: filters.sort_by || 'invited_at',
      sort_order: filters.sort_order || 'desc'
    };

    // Add search filter
    if (filters.search && filters.search.trim()) {
      apiParams.search = filters.search.trim();
    }

    // Add status filter
    if (filters.status && filters.status !== 'all') {
      apiParams.status = filters.status;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.INVITATIONS_LIST, apiParams);

    // Extract invitations data from response
    const invitationsData = response.data?.data?.invitations || response.data?.data || response.data || [];

    if (invitationsData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No invitations available to export with the current filters'
      };
    }

    // Generate filename with filters
    const filename = this.generateInvitationsFilename(format, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateInvitationsExcel(invitationsData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateInvitationsCSV(invitationsData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: invitationsData.length
    };
  }

  /**
   * Generate filename for user course progress export
   */
  generateUserCourseProgressFilename(
    format: 'csv' | 'excel',
    userName: string,
    filters: ExportUserCourseProgressParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];

    // Sanitize user name for filename
    const sanitizedUserName = userName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const filenameParts = [sanitizedUserName, 'course-progress'];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add status filter
    if (filters.status) {
      filenameParts.push(`status-${filters.status.toLowerCase()}`);
    }

    // Add sort filter
    if (filters.sort_by) {
      filenameParts.push(`sort-${filters.sort_by.toLowerCase()}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV from user course progress data
   */
  private generateUserCourseProgressCSV(courses: any[]): string {
    // Define headers
    const headers = [
      'Course Title',
      'Status',
      'Progress (%)',
      'Time Spent (HH:MM:SS)',
      'Deadline',
      'Assigned At',
      'Started At',
      'Completed At',
      'Last Accessed',
      'Mandatory'
    ];

    // Generate rows
    const rows = courses.map(course => [
      course.course_title || 'N/A',
      course.status || 'N/A',
      course.progress_percentage?.toFixed(2) || '0.00',
      this.formatTimeSpent(course.total_time_spent_seconds || 0),
      course.deadline_date ? this.formatDate(course.deadline_date) : 'No deadline',
      course.created_at ? this.formatDate(course.created_at) : 'N/A',
      course.started_at ? this.formatDate(course.started_at) : 'Not started',
      course.completed_at ? this.formatDate(course.completed_at) : 'Not completed',
      course.last_accessed_at ? this.formatDate(course.last_accessed_at) : 'Never',
      course.is_mandatory ? 'Yes' : 'No'
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from user course progress data
   */
  private generateUserCourseProgressExcel(courses: any[]): Blob {
    // Define headers
    const headers = [
      'Course Title',
      'Status',
      'Progress (%)',
      'Time Spent (HH:MM:SS)',
      'Deadline',
      'Assigned At',
      'Started At',
      'Completed At',
      'Last Accessed',
      'Mandatory'
    ];

    // Generate rows
    const rows = courses.map(course => [
      course.course_title || 'N/A',
      course.status || 'N/A',
      course.progress_percentage?.toFixed(2) || '0.00',
      this.formatTimeSpent(course.total_time_spent_seconds || 0),
      course.deadline_date ? this.formatDate(course.deadline_date) : 'No deadline',
      course.created_at ? this.formatDate(course.created_at) : 'N/A',
      course.started_at ? this.formatDate(course.started_at) : 'Not started',
      course.completed_at ? this.formatDate(course.completed_at) : 'Not completed',
      course.last_accessed_at ? this.formatDate(course.last_accessed_at) : 'Never',
      course.is_mandatory ? 'Yes' : 'No'
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 40 }, // Course Title
      { wch: 15 }, // Status
      { wch: 12 }, // Progress (%)
      { wch: 20 }, // Time Spent
      { wch: 18 }, // Deadline
      { wch: 18 }, // Assigned At
      { wch: 18 }, // Started At
      { wch: 18 }, // Completed At
      { wch: 18 }, // Last Accessed
      { wch: 12 }  // Mandatory
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Course Progress');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export user course progress to CSV or Excel
   * POST /api/admin/export/user-course-progress/:user_id
   */
  async exportUserCourseProgress(
    format: 'csv' | 'excel',
    userId: string,
    userName: string,
    filters: ExportUserCourseProgressParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params
    const apiParams: any = {
      sort_order: filters.sort_order || 'desc'
    };

    // Add filters
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.status) {
      apiParams.status = filters.status;
    }

    if (filters.course_id) {
      apiParams.course_id = filters.course_id;
    }

    if (filters.assignment_id) {
      apiParams.assignment_id = filters.assignment_id;
    }

    if (filters.sort_by) {
      apiParams.sort_by = filters.sort_by;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format === 'excel' ? 'EXCEL' : 'CSV';

    const response = await apiClient.post(
      API_ENDPOINTS.EXPORT.USER_COURSE_PROGRESS(userId),
      apiParams
    );

    // Extract course data from response
    const coursesData = response.data?.data?.data || response.data?.data || [];

    if (coursesData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No courses available to export with the current filters'
      };
    }

    // Generate filename with user name and filters
    const filename = this.generateUserCourseProgressFilename(format, userName, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateUserCourseProgressExcel(coursesData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateUserCourseProgressCSV(coursesData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: coursesData.length
    };
  }

  /**
   * Generate filename for user certification progress export
   */
  generateUserCertificationProgressFilename(
    format: 'csv' | 'excel',
    userName: string,
    filters: ExportUserCertificationProgressParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];

    // Sanitize user name for filename
    const sanitizedUserName = userName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const filenameParts = [sanitizedUserName, 'certification-progress'];

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add status filter
    if (filters.status) {
      filenameParts.push(`status-${filters.status.toLowerCase()}`);
    }

    // Add sort filter
    if (filters.sort_by) {
      filenameParts.push(`sort-${filters.sort_by.toLowerCase()}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV from user certification progress data
   */
  private generateUserCertificationProgressCSV(certifications: any[]): string {
    // Define headers
    const headers = [
      'Certification Name',
      'Status',
      'Exam Attempts',
      'Passed',
      'Time Spent (HH:MM:SS)',
      'Deadline',
      'Assigned At',
      'Started At',
      'Completed At',
      'Last Accessed',
      'Mandatory'
    ];

    // Generate rows
    const rows = certifications.map(cert => [
      cert.title || 'N/A',
      cert.status || 'N/A',
      cert.exam_attempts || 0,
      cert.passed_exam ? 'Yes' : 'No',
      this.formatTimeSpent(cert.total_time_spent_seconds || 0),
      cert.deadline_date ? this.formatDate(cert.deadline_date) : 'No deadline',
      cert.assigned_date ? this.formatDate(cert.assigned_date) : 'N/A',
      cert.started_at ? this.formatDate(cert.started_at) : 'Not started',
      cert.completed_at ? this.formatDate(cert.completed_at) : 'Not completed',
      cert.last_accessed_at ? this.formatDate(cert.last_accessed_at) : 'Never',
      cert.is_mandatory ? 'Yes' : 'No'
    ]);

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from user certification progress data
   */
  private generateUserCertificationProgressExcel(certifications: any[]): Blob {
    // Define headers
    const headers = [
      'Certification Name',
      'Status',
      'Exam Attempts',
      'Passed',
      'Time Spent (HH:MM:SS)',
      'Deadline',
      'Assigned At',
      'Started At',
      'Completed At',
      'Last Accessed',
      'Mandatory'
    ];

    // Generate rows
    const rows = certifications.map(cert => [
      cert.title || 'N/A',
      cert.status || 'N/A',
      cert.exam_attempts || 0,
      cert.passed_exam ? 'Yes' : 'No',
      this.formatTimeSpent(cert.total_time_spent_seconds || 0),
      cert.deadline_date ? this.formatDate(cert.deadline_date) : 'No deadline',
      cert.assigned_date ? this.formatDate(cert.assigned_date) : 'N/A',
      cert.started_at ? this.formatDate(cert.started_at) : 'Not started',
      cert.completed_at ? this.formatDate(cert.completed_at) : 'Not completed',
      cert.last_accessed_at ? this.formatDate(cert.last_accessed_at) : 'Never',
      cert.is_mandatory ? 'Yes' : 'No'
    ]);

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 40 }, // Certification Name
      { wch: 15 }, // Status
      { wch: 12 }, // Exam Attempts
      { wch: 10 }, // Passed
      { wch: 20 }, // Time Spent
      { wch: 18 }, // Deadline
      { wch: 18 }, // Assigned At
      { wch: 18 }, // Started At
      { wch: 18 }, // Completed At
      { wch: 18 }, // Last Accessed
      { wch: 12 }  // Mandatory
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Certification Progress');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export user certification progress to CSV or Excel
   * POST /api/admin/export/user-certification-progress/:user_id
   */
  async exportUserCertificationProgress(
    format: 'csv' | 'excel',
    userId: string,
    userName: string,
    filters: ExportUserCertificationProgressParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params
    const apiParams: any = {
      sort_order: filters.sort_order || 'desc'
    };

    // Add filters
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.status) {
      apiParams.status = filters.status;
    }

    if (filters.certification_id) {
      apiParams.certification_id = filters.certification_id;
    }

    if (filters.assignment_id) {
      apiParams.assignment_id = filters.assignment_id;
    }

    if (filters.sort_by) {
      apiParams.sort_by = filters.sort_by;
    }

    // Call export API endpoint - expecting JSON response
    apiParams._export_format = format === 'excel' ? 'EXCEL' : 'CSV';

    const response = await apiClient.post(
      API_ENDPOINTS.EXPORT.USER_CERTIFICATION_PROGRESS(userId),
      apiParams
    );

    // Extract certification data from response
    const certificationsData = response.data?.data?.data || response.data?.data || [];

    if (certificationsData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No certifications available to export with the current filters'
      };
    }

    // Generate filename with user name and filters
    const filename = this.generateUserCertificationProgressFilename(format, userName, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateUserCertificationProgressExcel(certificationsData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateUserCertificationProgressCSV(certificationsData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: certificationsData.length
    };
  }

  /**
   * Generate filename for audit trail export with filters
   */
  generateAuditTrailFilename(
    format: 'csv' | 'excel',
    filters: ExportAuditTrailParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['audit-trail-report'];

    // Add event type filter
    if (filters.event_type) {
      const sanitizedEventType = filters.event_type.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`event-${sanitizedEventType}`);
    }

    // Add date range OR timestamp
    if (filters.start_date && filters.end_date) {
      filenameParts.push(`${filters.start_date}-to-${filters.end_date}`);
    } else {
      // Only add timestamp if no date range filter
      filenameParts.push(timestamp);
    }

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Export audit trail as a streamed CSV file.
   *
   * Uses the server's streaming endpoint which writes CSV row-by-row using a
   * Mongoose cursor — server memory is constant regardless of result size, and
   * the browser saves bytes directly to disk (no JSON.parse, no XLSX.write).
   */
  // Build an .xlsx blob from audit-trail records (columns match the backend CSV export).
  private generateAuditTrailExcel(reports: any[]): Blob {
    const headers = [
      'Event Type', 'Event Time', 'User Name', 'User Email', 'Note',
      'Browser', 'Device/OS', 'IP Address', 'Old Data', 'New Data',
    ];

    const EXCEL_CELL_LIMIT = 32767;
    const cap = (value: string): string =>
      value.length > EXCEL_CELL_LIMIT ? `${value.slice(0, EXCEL_CELL_LIMIT - 14)}…[truncated]` : value;

    const rows = reports.map(r => [
      r.event_type || '',
      r.event_time ? this.formatDate(r.event_time) : '',
      r.action_by_info?.name || r.user_info?.name || r.meta_data?.user_name || '',
      r.action_by_info?.email || r.user_info?.email || r.meta_data?.user_email || r.data?.email || '',
      cap(r.note || r.event_description || ''),
      r.device_info?.browser?.name || r.browser || '',
      r.device_info?.os?.name || r.device || '',
      r.ip_address || r.data?.ip_address || '',
      cap(r.old_data ? JSON.stringify(r.old_data) : ''),
      cap(r.new_data ? JSON.stringify(r.new_data) : ''),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 20 }, { wch: 22 }, { wch: 28 }, { wch: 40 },
      { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 50 }, { wch: 50 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Trail');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async exportAuditTrail(
    format: 'csv' | 'excel',
    filters: ExportAuditTrailParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params — no skip/limit on export
    const apiParams: any = {};
    if (filters.event_type) apiParams.event_type = filters.event_type;
    if (filters.start_date) apiParams.start_date = filters.start_date;
    if (filters.end_date)   apiParams.end_date = filters.end_date;
    if (filters.email)      apiParams.email = filters.email;
    if (filters.name)       apiParams.name = filters.name;
    if (filters.search)     apiParams.search = filters.search;
    if (filters.include_archived) apiParams.include_archived = filters.include_archived;

    // ── Excel: fetch JSON from the data endpoint and build a real .xlsx client-side.
    // This endpoint records the export as EXCEL in the audit trail.
    if (format === 'excel') {
      const response = await apiClient.post(API_ENDPOINTS.AUDIT_TRAIL.EXPORT, apiParams);
      const payload = response.data?.data || response.data || {};
      const reports = payload.audit_trail_reports || [];
      if (!reports.length) {
        return {
          success: false,
          filename: '',
          count: 0,
          message: 'No audit trail records available to export with the current filters',
        };
      }
      // Excel is built in-browser and capped by the .xlsx 1,048,576-row limit. For very
      // large result sets, refuse Excel and direct the user to CSV (which streams, no limit)
      // — prevents a frozen/crashed tab.
      const MAX_EXCEL_ROWS = 100000;
      if (reports.length > MAX_EXCEL_ROWS) {
        return {
          success: false,
          filename: '',
          count: reports.length,
          message: `Too many records (${reports.length.toLocaleString()}) for Excel. Narrow the date range/filters, or use CSV export for large datasets.`,
        };
      }
      const excelBlob = this.generateAuditTrailExcel(reports);
      const filename = this.generateAuditTrailFilename('excel', filters);
      this.downloadExcel(excelBlob, filename);
      return { success: true, filename, count: reports.length };
    }

    // ── CSV: stream from the dedicated CSV endpoint (records the export as CSV).
    // Response body is raw text/csv bytes, not JSON.
    const response = await apiClient.post(
      API_ENDPOINTS.AUDIT_TRAIL.EXPORT_CSV,
      apiParams,
      { responseType: 'blob' }
    );

    const blob = response.data as Blob;

    // Header-only or empty body means no records matched.
    // Header is ~80 bytes; treat anything below ~120 bytes as "no data rows".
    if (!blob || blob.size <= 120) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No audit trail records available to export with the current filters',
      };
    }

    const baseFilename = this.generateAuditTrailFilename('csv', filters);
    const filename = baseFilename.replace(/\.xlsx?$/i, '.csv');

    // downloadExcel is a generic blob-download helper despite its name (uses Blob URL + <a download>)
    this.downloadExcel(blob, filename);

    return {
      success: true,
      filename,
      // Row count not known on the client (server didn't send it). -1 signals "unknown".
      count: -1,
    };
  }

  
/**
   * Generate filename for reviews export with filters
   */
  generateReviewsFilename(
    format: 'csv' | 'excel',
    filters: ExportReviewsParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['reviews'];

    // Add review type filter
    if (filters.review_type) {
      filenameParts.push(filters.review_type.replace('_', '-'));
    }

    // Add status filter
    if (filters.status) {
      filenameParts.push(filters.status);
    }

    // Add rating filter
    if (filters.rating) {
      filenameParts.push(`${filters.rating}-star`);
    }

    // Add search query if applied
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    // Add file extension
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Get review type label
   */
  private getReviewTypeLabel(reviewType: string): string {
    const labels: Record<string, string> = {
      'course': 'Course',
      'certificate': 'Certificate',
      'learning_path': 'Learning Path'
    };
    return labels[reviewType] || reviewType;
  }

  /**
   * Get review status label
   */
  private getReviewStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'approved': 'Approved',
      'flagged': 'Flagged',
      'deleted': 'Deleted'
    };
    return labels[status] || status;
  }

  /**
   * Generate CSV from reviews data
   */
  private generateReviewsCSV(reviews: any[]): string {
    // Define headers
    const headers = [
      'User Name',
      'User Email',
      'Employee ID',
      'Target',
      'Type',
      'Rating',
      'Review Text',
      'Status',
      'Featured',
      'Helpful Count',
      'Report Count',
      'Admin Response',
      'Created At'
    ];

    // Generate rows
    const rows = reviews.map(review => {
      const userName = `${review.user_details?.first_name || ''} ${review.user_details?.last_name || ''}`.trim() || 'Unknown';

      return [
        userName,
        review.user_details?.email || '',
        review.user_details?.empl_id || '',
        review.entity_name || 'N/A',
        this.getReviewTypeLabel(review.review_type),
        review.rating || 0,
        review.review_text || '',
        this.getReviewStatusLabel(review.status),
        review.is_featured ? 'Yes' : 'No',
        review.helpful_count || 0,
        review.report_count || 0,
        review.admin_response?.text || '',
        review.createdAt ? this.formatDate(review.createdAt) : ''
      ];
    });

    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from reviews data
   */
  private generateReviewsExcel(reviews: any[]): Blob {
    // Define headers
    const headers = [
      'User Name',
      'User Email',
      'Employee ID',
      'Target',
      'Type',
      'Rating',
      'Review Text',
      'Status',
      'Featured',
      'Helpful Count',
      'Report Count',
      'Admin Response',
      'Created At'
    ];

    // Generate rows
    const rows = reviews.map(review => {
      const userName = `${review.user_details?.first_name || ''} ${review.user_details?.last_name || ''}`.trim() || 'Unknown';

      return [
        userName,
        review.user_details?.email || '',
        review.user_details?.empl_id || '',
        review.entity_name || 'N/A',
        this.getReviewTypeLabel(review.review_type),
        review.rating || 0,
        review.review_text || '',
        this.getReviewStatusLabel(review.status),
        review.is_featured ? 'Yes' : 'No',
        review.helpful_count || 0,
        review.report_count || 0,
        review.admin_response?.text || '',
        review.createdAt ? this.formatDate(review.createdAt) : ''
      ];
    });

    // Create worksheet data with headers
    const worksheetData = [headers, ...rows];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 25 }, // User Name
      { wch: 30 }, // User Email
      { wch: 15 }, // Employee ID
      { wch: 40 }, // Target
      { wch: 15 }, // Type
      { wch: 8 },  // Rating
      { wch: 50 }, // Review Text
      { wch: 12 }, // Status
      { wch: 10 }, // Featured
      { wch: 12 }, // Helpful Count
      { wch: 12 }, // Report Count
      { wch: 40 }, // Admin Response
      { wch: 20 }  // Created At
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reviews');

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create blob
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export reviews to CSV or Excel
   * GET /api/reviews/admin/export
   */
  async exportReviews(
    format: 'csv' | 'excel',
    filters: ExportReviewsParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    // Build API params - no skip/limit
    const apiParams: any = {};

    // Add filters
    if (filters.review_type) {
      apiParams.review_type = filters.review_type;
    }

    if (filters.status) {
      apiParams.status = filters.status;
    }

    if (filters.rating) {
      apiParams.rating = filters.rating;
    }

    if (filters.search) {
      apiParams.search = filters.search;
    }

    // Call export API endpoint - expecting JSON response
    const response = await apiClient.get(API_ENDPOINTS.REVIEWS.ADMIN_EXPORT, { params: apiParams });

    // Extract reviews data from response
    const reviewsData = response.data?.data?.reviews || response.data?.reviews || [];

    if (reviewsData.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No reviews available to export with the current filters'
      };
    }

    // Generate filename with filters
    const filename = this.generateReviewsFilename(format, filters);

    // Generate and download file based on format
    if (format === 'excel') {
      const excelBlob = this.generateReviewsExcel(reviewsData);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateReviewsCSV(reviewsData);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: reviewsData.length
    };
  }

  /**
   * Generate filename for certification performance export with filters
   */
  generateCertificationPerformanceFilename(
    format: 'csv' | 'excel',
    filters: ExportCertificationPerformanceParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['certification-performance'];

    // Add time range/granularity
    if (filters.timeRange === 'custom' && filters.start_date && filters.end_date) {
      filenameParts.push(`custom-${filters.start_date}-to-${filters.end_date}`);
    } else if (filters.timeRange && filters.timeRange !== 'all') {
      filenameParts.push(filters.timeRange);
    } else {
      filenameParts.push('all-time');
    }

    // Add status filter
    if (filters.status && filters.status !== 'all') {
      filenameParts.push(`status-${filters.status}`);
    }

    // Add search query
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add department filter
    if (filters.groupKey) {
      filenameParts.push(`dept-${filters.groupKey}`);
    }

    // Add role filter
    if (filters.roleKey) {
      filenameParts.push(`role-${filters.roleKey}`);
    }

    // Add sort filter
    if (filters.sortBy) {
      filenameParts.push(`sort-${filters.sortBy}-${filters.sortOrder || 'asc'}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV from certification performance data
   */
  private generateCertificationPerformanceCSV(certifications: any[]): string {
    const headers = [
      'Certification Name',
      'Status',
      'Total Enrollments',
      'Completed Users',
      'In Progress Users',
      'Not Started Users',
      'Passed Users',
      'Failed Users',
      'Pass Rate (%)',
      'Average Score'
    ];

    const rows = certifications.map(cert => [
      cert.certification_name || 'N/A',
      cert.status || 'N/A',
      cert.total_user_enrollments || 0,
      cert.completed_user_count || 0,
      cert.in_progress_user_count || 0,
      cert.not_started_user_count || 0,
      cert.passed_user_count || 0,
      cert.failed_user_count || 0,
      cert.pass_percentage?.toFixed(2) || '0.00',
      cert.avg_score?.toFixed(2) || '0.00'
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from certification performance data
   */
  private generateCertificationPerformanceExcel(certifications: any[]): Blob {
    const headers = [
      'Certification Name',
      'Status',
      'Total Enrollments',
      'Completed Users',
      'In Progress Users',
      'Not Started Users',
      'Passed Users',
      'Failed Users',
      'Pass Rate (%)',
      'Average Score'
    ];

    const rows = certifications.map(cert => [
      cert.certification_name || 'N/A',
      cert.status || 'N/A',
      cert.total_user_enrollments || 0,
      cert.completed_user_count || 0,
      cert.in_progress_user_count || 0,
      cert.not_started_user_count || 0,
      cert.passed_user_count || 0,
      cert.failed_user_count || 0,
      cert.pass_percentage?.toFixed(2) || '0.00',
      cert.avg_score?.toFixed(2) || '0.00'
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    worksheet['!cols'] = [
      { wch: 40 }, // Certification Name
      { wch: 12 }, // Status
      { wch: 18 }, // Total Enrollments
      { wch: 18 }, // Completed Users
      { wch: 18 }, // In Progress Users
      { wch: 18 }, // Not Started Users
      { wch: 15 }, // Passed Users
      { wch: 15 }, // Failed Users
      { wch: 15 }, // Pass Rate (%)
      { wch: 15 }, // Average Score
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Certification Performance');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export certification performance data to CSV or Excel
   * Calls the backend export API (no pagination) and generates file from JSON response
   */
  async exportCertificationPerformance(
    format: 'csv' | 'excel',
    filters: ExportCertificationPerformanceParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const apiParams: any = {};

    if (filters.status && filters.status !== 'all') {
      apiParams.status = filters.status;
    }
    if (filters.search) {
      apiParams.search = filters.search;
    }
    if (filters.sortBy) {
      apiParams.sortBy = filters.sortBy;
    }
    if (filters.sortOrder) {
      apiParams.sortOrder = filters.sortOrder;
    }
    if (filters.timeRange === 'custom' && filters.start_date && filters.end_date) {
      apiParams.start_date = filters.start_date;
      apiParams.end_date = filters.end_date;
    } else if (filters.timeRange && filters.timeRange !== 'all' && filters.timeRange !== 'custom') {
      apiParams.granularity = filters.timeRange;
    }
    if (filters.groupKey) {
      apiParams.groupKey = filters.groupKey;
    }
    if (filters.roleKey) {
      apiParams.roleKey = filters.roleKey;
    }

    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.CERTIFICATION_PERFORMANCE, apiParams);

    const certifications = response.data?.data?.certifications || [];

    if (certifications.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No certification performance data available to export with the current filters'
      };
    }

    const filename = this.generateCertificationPerformanceFilename(format, filters);

    if (format === 'excel') {
      const excelBlob = this.generateCertificationPerformanceExcel(certifications);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateCertificationPerformanceCSV(certifications);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: certifications.length
    };
  }
  // ==================== Course Performance Export ====================

  generateCoursePerformanceFilename(
    format: 'csv' | 'excel',
    filters: ExportCoursePerformanceParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['course-performance'];

    // Add time range/granularity
    if (filters.timeRange === 'custom' && filters.start_date && filters.end_date) {
      filenameParts.push(`custom-${filters.start_date}-to-${filters.end_date}`);
    } else if (filters.timeRange && filters.timeRange !== 'all') {
      filenameParts.push(filters.timeRange);
    } else {
      filenameParts.push('all-time');
    }

    // Add status filter
    if (filters.status && filters.status !== 'all') {
      filenameParts.push(`status-${filters.status}`);
    }

    // Add search query
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add category filter
    if (filters.categoryName) {
      const sanitizedCategory = filters.categoryName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`cat-${sanitizedCategory}`);
    }

    // Add department filter
    if (filters.groupKey) {
      filenameParts.push(`dept-${filters.groupKey}`);
    }

    // Add role filter
    if (filters.roleKey) {
      filenameParts.push(`role-${filters.roleKey}`);
    }

    // Add sort filter
    if (filters.sortBy) {
      filenameParts.push(`sort-${filters.sortBy}-${filters.sortOrder || 'asc'}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  private generateCoursePerformanceCSV(courses: any[]): string {
    const headers = [
      'Course Name',
      'Status',
      'Category',
      'Total Enrollments',
      'Completed Users',
      'In Progress Users',
      'Not Started Users',
      'Completion Rate (%)'
    ];

    const rows = courses.map(course => [
      course.course_name || 'N/A',
      course.status || 'N/A',
      course.category_name || 'N/A',
      course.total_user_enrollments || 0,
      course.completed_user_count || 0,
      course.in_progress_user_count || 0,
      course.not_started_user_count || 0,
      course.completion_percentage?.toFixed(2) || '0.00'
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  private generateCoursePerformanceExcel(courses: any[]): Blob {
    const headers = [
      'Course Name',
      'Status',
      'Category',
      'Total Enrollments',
      'Completed Users',
      'In Progress Users',
      'Not Started Users',
      'Completion Rate (%)'
    ];

    const rows = courses.map(course => [
      course.course_name || 'N/A',
      course.status || 'N/A',
      course.category_name || 'N/A',
      course.total_user_enrollments || 0,
      course.completed_user_count || 0,
      course.in_progress_user_count || 0,
      course.not_started_user_count || 0,
      course.completion_percentage?.toFixed(2) || '0.00'
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    worksheet['!cols'] = [
      { wch: 40 }, // Course Name
      { wch: 12 }, // Status
      { wch: 25 }, // Category
      { wch: 18 }, // Total Enrollments
      { wch: 18 }, // Completed Users
      { wch: 18 }, // In Progress Users
      { wch: 18 }, // Not Started Users
      { wch: 18 }, // Completion Rate (%)
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Course Performance');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  async exportCoursePerformance(
    format: 'csv' | 'excel',
    filters: ExportCoursePerformanceParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const apiParams: any = {};

    if (filters.status && filters.status !== 'all') {
      apiParams.status = filters.status;
    }
    if (filters.search) {
      apiParams.search = filters.search;
    }
    if (filters.sortBy) {
      apiParams.sortBy = filters.sortBy;
    }
    if (filters.sortOrder) {
      apiParams.sortOrder = filters.sortOrder;
    }
    if (filters.timeRange === 'custom' && filters.start_date && filters.end_date) {
      apiParams.start_date = filters.start_date;
      apiParams.end_date = filters.end_date;
    } else if (filters.timeRange && filters.timeRange !== 'all' && filters.timeRange !== 'custom') {
      apiParams.granularity = filters.timeRange;
    }
    if (filters.groupKey) {
      apiParams.groupKey = filters.groupKey;
    }
    if (filters.roleKey) {
      apiParams.roleKey = filters.roleKey;
    }
    if (filters.categoryId) {
      apiParams.categoryId = filters.categoryId;
    }

    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.COURSE_PERFORMANCE, apiParams);

    const courses = response.data?.data?.courses || [];

    if (courses.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No course performance data available to export with the current filters'
      };
    }

    const filename = this.generateCoursePerformanceFilename(format, filters);

    if (format === 'excel') {
      const excelBlob = this.generateCoursePerformanceExcel(courses);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateCoursePerformanceCSV(courses);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: courses.length
    };
  }

  // ==================== Learning Path Performance Export ====================

  generateLearningPathPerformanceFilename(
    format: 'csv' | 'excel',
    filters: ExportLearningPathPerformanceParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['learning-path-performance'];

    // Add time range/granularity
    if (filters.timeRange === 'custom' && filters.start_date && filters.end_date) {
      filenameParts.push(`custom-${filters.start_date}-to-${filters.end_date}`);
    } else if (filters.timeRange && filters.timeRange !== 'all') {
      filenameParts.push(filters.timeRange);
    } else {
      filenameParts.push('all-time');
    }

    // Add status filter
    if (filters.status && filters.status !== 'all') {
      filenameParts.push(`status-${filters.status}`);
    }

    // Add search query
    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    // Add category filter
    if (filters.categoryName) {
      const sanitizedCategory = filters.categoryName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`cat-${sanitizedCategory}`);
    }

    // Add department filter
    if (filters.groupKey) {
      filenameParts.push(`dept-${filters.groupKey}`);
    }

    // Add role filter
    if (filters.roleKey) {
      filenameParts.push(`role-${filters.roleKey}`);
    }

    // Add sort filter
    if (filters.sortBy) {
      filenameParts.push(`sort-${filters.sortBy}-${filters.sortOrder || 'asc'}`);
    }

    // Add timestamp
    filenameParts.push(timestamp);

    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  private generateLearningPathPerformanceCSV(learningPaths: any[]): string {
    const headers = [
      'Learning Path Name',
      'Status',
      'Total Enrollments',
      'Completed Users',
      'In Progress Users',
      'Not Started Users',
      'Completion Rate (%)',
      'Total Courses'
    ];

    const rows = learningPaths.map(lp => [
      lp.learning_path_title || 'N/A',
      lp.status || 'N/A',
      lp.total_user_enrollments || 0,
      lp.completed_users || 0,
      lp.in_progress_users || 0,
      lp.not_started_users || 0,
      lp.completion_percentage?.toFixed(2) || '0.00',
      lp.total_courses || 0
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  private generateLearningPathPerformanceExcel(learningPaths: any[]): Blob {
    const headers = [
      'Learning Path Name',
      'Status',
      'Total Enrollments',
      'Completed Users',
      'In Progress Users',
      'Not Started Users',
      'Completion Rate (%)',
      'Total Courses'
    ];

    const rows = learningPaths.map(lp => [
      lp.learning_path_title || 'N/A',
      lp.status || 'N/A',
      lp.total_user_enrollments || 0,
      lp.completed_users || 0,
      lp.in_progress_users || 0,
      lp.not_started_users || 0,
      lp.completion_percentage?.toFixed(2) || '0.00',
      lp.total_courses || 0
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    worksheet['!cols'] = [
      { wch: 40 }, // Learning Path Name
      { wch: 12 }, // Status
      { wch: 18 }, // Total Enrollments
      { wch: 18 }, // Completed Users
      { wch: 18 }, // In Progress Users
      { wch: 18 }, // Not Started Users
      { wch: 18 }, // Completion Rate (%)
      { wch: 15 }, // Total Courses
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Learning Path Performance');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  async exportLearningPathPerformance(
    format: 'csv' | 'excel',
    filters: ExportLearningPathPerformanceParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const apiParams: any = {};

    if (filters.status && filters.status !== 'all') {
      apiParams.status = filters.status;
    }
    if (filters.search) {
      apiParams.search = filters.search;
    }
    if (filters.sortBy) {
      apiParams.sortBy = filters.sortBy;
    }
    if (filters.sortOrder) {
      apiParams.sortOrder = filters.sortOrder;
    }
    if (filters.timeRange === 'custom' && filters.start_date && filters.end_date) {
      apiParams.start_date = filters.start_date;
      apiParams.end_date = filters.end_date;
    } else if (filters.timeRange && filters.timeRange !== 'all' && filters.timeRange !== 'custom') {
      apiParams.granularity = filters.timeRange;
    }
    if (filters.groupKey) {
      apiParams.groupKey = filters.groupKey;
    }
    if (filters.roleKey) {
      apiParams.roleKey = filters.roleKey;
    }
    if (filters.categoryId) {
      apiParams.categoryId = filters.categoryId;
    }

    apiParams._export_format = format;
    const response = await apiClient.post(API_ENDPOINTS.EXPORT.LEARNING_PATH_PERFORMANCE, apiParams);

    const learningPaths = response.data?.data?.learning_paths || [];

    if (learningPaths.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No learning path performance data available to export with the current filters'
      };
    }

    const filename = this.generateLearningPathPerformanceFilename(format, filters);

    if (format === 'excel') {
      const excelBlob = this.generateLearningPathPerformanceExcel(learningPaths);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateLearningPathPerformanceCSV(learningPaths);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: learningPaths.length
    };
  }

  /**
   * Generate filename for approver requests export with filters
   */
  generateApproverRequestsFilename(
    format: 'csv' | 'excel',
    filters: ExportApproverRequestsParams
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const filenameParts = ['ticket-requests'];

    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      filenameParts.push(`search-${sanitizedSearch}`);
    }

    if (filters.status) {
      filenameParts.push(`status-${filters.status.toLowerCase()}`);
    }

    if (filters.item_type) {
      filenameParts.push(`type-${filters.item_type.toLowerCase()}`);
    }

    if (filters.my_requests_only) {
      filenameParts.push('my-requests');
    }

    filenameParts.push(timestamp);

    const extension = format === 'excel' ? 'xlsx' : 'csv';
    return filenameParts.join('_') + `.${extension}`;
  }

  /**
   * Generate CSV content for approver requests
   */
  private generateApproverRequestsCSV(data: any[]): string {
    // Find max items across all tickets to determine number of "Requested For" columns
    const maxItems = data.reduce((max, ticket) => {
      if (ticket.item_type === 'GENERAL') return Math.max(max, 1);
      return Math.max(max, (ticket.items || []).length || 1);
    }, 1);

    const headers = [
      'Ticket Number',
      'Requester Name',
      'Requester Email',
      'Type',
      ...Array.from({ length: maxItems }, (_, i) => maxItems === 1 ? 'Requested For' : `Requested For ${i + 1}`),
      'Status',
      'Requested Date',
    ];

    const rows = data.map((ticket: any) => {
      // Build item cells
      const itemCells: string[] = [];
      if (ticket.item_type === 'GENERAL') {
        itemCells.push(ticket.general_description || '');
      } else {
        const items = (ticket.items || []);
        for (const item of items) {
          itemCells.push(item.item_name || '');
        }
      }
      // Pad to maxItems
      while (itemCells.length < maxItems) {
        itemCells.push('');
      }

      return [
        ticket.ticket_number || '',
        ticket.requester_name || '',
        ticket.requester_email || '',
        ticket.item_type || '',
        ...itemCells,
        ticket.status || '',
        ticket.createdAt ? new Date(ticket.createdAt).toISOString().split('T')[0] : '',
      ];
    });

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => this.sanitizeCsvCell(cell)).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel workbook for approver requests
   */
  private generateApproverRequestsExcel(data: any[]): Blob {
    // Find max items across all tickets
    const maxItems = data.reduce((max, ticket) => {
      if (ticket.item_type === 'GENERAL') return Math.max(max, 1);
      return Math.max(max, (ticket.items || []).length || 1);
    }, 1);

    const rows = data.map((ticket: any) => {
      const row: Record<string, string> = {
        'Ticket Number': ticket.ticket_number || '',
        'Requester Name': ticket.requester_name || '',
        'Requester Email': ticket.requester_email || '',
        'Type': ticket.item_type || '',
      };

      // Add item columns
      if (ticket.item_type === 'GENERAL') {
        const colName = maxItems === 1 ? 'Requested For' : 'Requested For 1';
        row[colName] = ticket.general_description || '';
        for (let i = 1; i < maxItems; i++) {
          row[`Requested For ${i + 1}`] = '';
        }
      } else {
        const items = ticket.items || [];
        for (let i = 0; i < maxItems; i++) {
          const colName = maxItems === 1 ? 'Requested For' : `Requested For ${i + 1}`;
          row[colName] = items[i]?.item_name || '';
        }
      }

      row['Status'] = ticket.status || '';
      row['Requested Date'] = ticket.createdAt ? new Date(ticket.createdAt).toISOString().split('T')[0] : '';

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ticket Requests');

    const colWidths = [
      { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 15 },
      ...Array.from({ length: maxItems }, () => ({ wch: 30 })),
      { wch: 12 }, { wch: 12 },
    ];
    worksheet['!cols'] = colWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Export approver requests to CSV or Excel
   */
  async exportApproverRequests(
    format: 'csv' | 'excel',
    filters: ExportApproverRequestsParams
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const apiParams: any = {};

    if (filters.search) apiParams.search = filters.search;
    if (filters.status) apiParams.status = filters.status;
    if (filters.item_type) apiParams.item_type = filters.item_type;
    if (filters.approver_user_id) apiParams.approver_user_id = filters.approver_user_id;
    if (filters.my_requests_only !== undefined) apiParams.my_requests_only = filters.my_requests_only;
    apiParams._export_format = format === 'excel' ? 'EXCEL' : 'CSV';

    const response = await apiClient.post(API_ENDPOINTS.EXPORT.APPROVER_REQUESTS, apiParams);
    const data = response.data?.data || response.data || [];

    if (data.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No ticket requests found to export with the current filters'
      };
    }

    const filename = this.generateApproverRequestsFilename(format, filters);

    if (format === 'excel') {
      const excelBlob = this.generateApproverRequestsExcel(data);
      this.downloadExcel(excelBlob, filename);
    } else {
      const csvContent = this.generateApproverRequestsCSV(data);
      this.downloadCSV(csvContent, filename);
    }

    return {
      success: true,
      filename,
      count: data.length
    };
  }
}

export interface ExportApproverRequestsParams {
  search?: string;
  status?: string;
  item_type?: string;
  approver_user_id?: string;
  my_requests_only?: boolean;
}

export interface ExportCoursePerformanceParams {
  timeRange?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  start_date?: string;
  end_date?: string;
  groupKey?: string;
  roleKey?: string;
  categoryId?: string;
  categoryName?: string;
}

export interface ExportLearningPathPerformanceParams {
  timeRange?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  start_date?: string;
  end_date?: string;
  groupKey?: string;
  roleKey?: string;
  categoryId?: string;
  categoryName?: string;
}

export const exportService = new ExportService();
